import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { GraduationCap, Mail, Download, CheckCircle } from 'lucide-react';
import { sendEmail } from '@/utils/emailService';
import { sanitizeInput, validateEmail, rateLimitCheck } from '@/utils/security';

const TrainingenPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    school: '',
    email: ''
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'Voornaam is verplicht';
    }
    
    if (!formData.school.trim()) {
      errors.school = 'School of organisatie is verplicht';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'E-mailadres is verplicht';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Voer een geldig e-mailadres in';
    }
    
    return errors;
  };

  const handleInputChange = (field, value) => {
    // No sanitization during typing - only basic input handling
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Rate limiting check
    if (!rateLimitCheck()) {
      toast({
        title: "Even geduld! ⏰",
        description: "Wacht 1 seconde voordat je opnieuw een aanvraag verstuurt.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Sanitize data for email submission
      const sanitizedData = {
        firstName: sanitizeInput(formData.firstName),
        lastName: sanitizeInput(formData.lastName),
        school: sanitizeInput(formData.school),
        email: sanitizeInput(formData.email)
      };

      // EmailJS template parameters - exact wat jouw template verwacht
      const templateParams = {
        name: `${sanitizedData.firstName} ${sanitizedData.lastName}`.trim(),
        email: sanitizedData.email,
        message: `Brochure aanvraag voor ${sanitizedData.school}`,
        title: "Brochure Aanvraag - Classbuddy Training",
        time: new Date().toLocaleString('nl-NL')
      };
      
      const emailResult = await sendEmail(templateParams);
      
      if (emailResult.success) {
        // Show success message
        setShowSuccessMessage(true);
        
        // Start download with new brochure link
        const downloadLink = document.createElement('a');
        downloadLink.href = 'https://drive.google.com/uc?export=download&id=1-SfpZjKXfULcH4UjbZH_bz8gemLbXUYM';
        downloadLink.download = 'Classbuddy_Trainingen_Brochure.pdf';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        
        toast({
          title: "Je aanvraag is verzonden! ✅",
          description: "Je brochure aanvraag is automatisch naar ons gestuurd en de download is gestart!",
          duration: 6000,
        });
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
          setFormData({
            firstName: '',
            lastName: '',
            school: '',
            email: ''
          });
        }, 3000);
      } else {
        throw new Error(emailResult.error || 'Email kon niet verzonden worden');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "EmailJS configuratie vereist ⚙️",
        description: "Download gestart, maar email service moet nog volledig geconfigureerd worden. Controleer de console voor instructies.",
        variant: "destructive",
        duration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const trainings = [
    {
      buddy: "Nouri",
      theme: "Vriendschappen & Groepsvorming",
      training: "Leer sociale vaardigheden en bevorder inclusie, geïnspireerd door Nouri's expertise in vriendschap.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/36bcc828609374cccfea37437b0087b4.png",
      gradientFrom: "from-blue-400",
      gradientTo: "to-blue-600"
    },
    {
      buddy: "Luma",
      theme: "Angst & Spanning",
      training: "Ontdek hoe je leerlingen helpt omgaan met spanning en stress, met Luma's kalmerende methoden.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/64410c4a8456aa01d48bf1fcccc62851.png",
      gradientFrom: "from-yellow-400",
      gradientTo: "to-yellow-600"
    },
    {
      buddy: "Rafi",
      theme: "Zelfvertrouwen & Keuzes",
      training: "Versterk leerlingcoaching en motivatie, gebaseerd op Rafi's aanpak voor zelfvertrouwen en keuzes.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/e0628690ae18b65afa2ea44c952b4171.png",
      gradientFrom: "from-purple-400",
      gradientTo: "to-purple-600"
    },
    {
      buddy: "Zena",
      theme: "Emotieregulatie",
      training: "Beheers emotieherkenning en gespreksvoering, geïnspireerd door Zena's luisterend oor.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/5be273ac8daf84ec650dbc720f5bc36f.png",
      gradientFrom: "from-pink-400",
      gradientTo: "to-pink-600"
    },
    {
      buddy: "Appie",
      theme: "Pesten & Weerbaarheid",
      training: "Focus op signalering en preventie van pesten, met Appie's methoden voor weerbaarheid.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/32a7bab3949898c8b91390aea3b3cebc.png",
      gradientFrom: "from-green-400",
      gradientTo: "to-green-600"
    },
    {
      buddy: "Chloe",
      theme: "Thuissituatie & Veranderingen",
      training: "Begeleid kinderen bij thuissituaties en veranderingen, met Chloe's begripvolle aanpak.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/6fa70f008d66b5c3004d0717a1bb1874.png",
      gradientFrom: "from-orange-400",
      gradientTo: "to-orange-600"
    }
  ];

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-16"
      >
        <motion.div variants={fadeIn(0,30)} className="flex justify-center mb-8">
          <GraduationCap size={72} className="text-cm-blue" />
        </motion.div>
        <motion.h1
          variants={fadeIn(0.1, 30)}
          className="page-title"
        >
          Meer kennis, meer <span className="text-gradient-pink-blue font-medium">impact in de klas.</span>
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle"
        >
          Onze pedagogische trainingen zijn ontwikkeld om onderwijsprofessionals te versterken in het herkennen, begrijpen en begeleiden van sociaal-emotionele thema's in de klas. Elke training sluit aan op de expertise van één van onze buddy's. Zo bouwen we samen aan een schoolklimaat waar ieder kind zich gehoord, gezien en gesteund voelt.
        </motion.p>
      </motion.div>

      <motion.div
        variants={fadeIn(0.3, 20)}
        initial="hidden"
        animate="visible"
        className="p-10 bg-gradient-to-r from-cm-pink-light/70 via-cm-yellow-light/70 to-cm-green-light/70 rounded-3xl shadow-2xl mb-16"
      >
        <div className="text-center mb-12">
          <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-5">🎓 Pedagogische Trainingen</h2>
          <p className="text-lg text-cm-gray max-w-2xl mx-auto mb-4 leading-relaxed">
            Classbuddy is meer dan een digitale tool. Voor scholen die écht werk willen maken van sociaal-emotionele ondersteuning, bieden wij aanvullende pedagogische trainingen.
          </p>
          <p className="text-lg text-cm-gray max-w-2xl mx-auto mb-10 leading-relaxed">
            Deze sluiten aan op de thema's van onze buddy's en versterken het handelingsrepertoire van leerkrachten, intern begeleiders en zorgcoördinatoren.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {trainings.map((training, index) => (
            <motion.div
              key={index}
              variants={fadeIn(0.4 + index * 0.1, 15)}
              initial="hidden"
              animate="visible"
              className={`glassmorphism-card p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br ${training.gradientFrom} ${training.gradientTo}`}
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <div className="flex items-center mb-4">
                <motion.div 
                  className="w-16 h-16 bg-white/30 rounded-full p-2 mr-4"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <img 
                    src={training.image} 
                    alt={`${training.buddy} buddy icon`}
                    className="w-full h-full object-contain rounded-full"
                  />
                </motion.div>
                <div>
                  <h3 className="text-xl font-medium text-white mb-1">{training.buddy}</h3>
                  <p className="text-white/90 text-sm">{training.theme}</p>
                </div>
              </div>
              <p className="text-white/95 text-base">{training.training}</p>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-xl font-semibold text-cm-blue-dark mb-6">
            📩 Interesse in een training op school?
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-cm-blue-dark hover:text-cm-blue border-2 border-cm-blue hover:border-cm-blue-dark font-medium text-lg px-8 py-6 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300 group"
          >
            <a href="mailto:info@classbuddy.nl">
              <Mail className="inline-block mr-2 h-5 w-5" />
              Contact via info@classbuddy.nl
            </a>
          </Button>
        </div>
      </motion.div>

      <motion.div
        variants={fadeIn(0.8, 20)}
        initial="hidden"
        animate="visible"
        className="p-8 rounded-2xl shadow-lg"
        style={{ backgroundColor: '#F3F7FB' }}
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-sans font-medium text-cm-blue-dark mb-4">Vraag de trainingsbrochure aan</h2>
          <p className="text-base text-cm-gray max-w-2xl mx-auto leading-relaxed">
            Wil je alle info in één overzicht? Vul hieronder je naam, school en e-mailadres in. Na het verzenden begint de trainingsbrochure automatisch met downloaden en ontvangen wij je aanvraag.
          </p>
        </div>

        {showSuccessMessage ? (
          <div className="text-center p-8 bg-green-50 rounded-xl border border-green-200">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-green-700 mb-2">Bedankt!</h3>
            <p className="text-green-600">Je aanvraag is automatisch verzonden en de download start nu.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="firstName" className="block text-sm font-medium text-cm-blue-dark mb-2">
                  Voornaam *
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full ${formErrors.firstName ? 'border-red-500' : ''}`}
                  placeholder="Je voornaam"
                  disabled={isSubmitting}
                />
                {formErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.firstName}</p>
                )}
              </div>

              <div>
                <Label htmlFor="lastName" className="block text-sm font-medium text-cm-blue-dark mb-2">
                  Achternaam
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full"
                  placeholder="Je achternaam"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="school" className="block text-sm font-medium text-cm-blue-dark mb-2">
                School of organisatie *
              </Label>
              <Input
                id="school"
                type="text"
                value={formData.school}
                onChange={(e) => handleInputChange('school', e.target.value)}
                className={`w-full ${formErrors.school ? 'border-red-500' : ''}`}
                placeholder="Naam van je school of organisatie"
                disabled={isSubmitting}
              />
              {formErrors.school && (
                <p className="text-red-500 text-sm mt-1">{formErrors.school}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="block text-sm font-medium text-cm-blue-dark mb-2">
                E-mailadres *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full ${formErrors.email ? 'border-red-500' : ''}`}
                placeholder="je@email.nl"
                disabled={isSubmitting}
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>

            <div className="text-center">
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="bg-cm-blue hover:bg-cm-blue-dark text-white font-medium text-lg px-8 py-4 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <Download className="inline-block mr-2 h-5 w-5" />
                {isSubmitting ? 'Verzenden...' : '📩 Download trainingsbrochure'}
              </Button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default TrainingenPage;
